// MIDI-Eingang über Web MIDI (Chrome, Edge): Keyboard anschließen, live über das Instrument der Spur spielen (Thru)
// und aufnehmen (Pro Tools Kap. 24). Sustain-Pedal (CC 64) verlängert die Noten beim Aufnehmen.
// Zeitstempel kommen in performance.now()-Zeit und werden beim Anlegen der Clips auf die gehörte Projektzeit umgerechnet.
import { engine } from './engine.ts';
import { voiceFor } from './instruments.ts';
import { useStudio } from '../state/store.ts';

export interface MidiDevice { id: string; name: string; manufacturer: string }
/** Aufgenommene Note in performance.now()-Zeit (ms) */
export interface RawNote { m: number; v: number; on: number; off: number }

const PREFS = 'srs.midiIn';
interface Prefs { thru: boolean; disabled: string[] }
const loadPrefs = (): Prefs => { try { return { thru: true, disabled: [], ...JSON.parse(localStorage.getItem(PREFS) ?? '{}') }; } catch { return { thru: true, disabled: [] }; } };

class MidiIn {
  supported = typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator;
  access: MIDIAccess | null = null;
  devices: MidiDevice[] = [];
  error = '';
  prefs = loadPrefs();
  /** letzte Aktivität je Gerät (performance.now) für die Anzeige */
  activity = new Map<string, number>();
  lastNote: { m: number; v: number } | null = null;
  private listeners = new Set<() => void>();
  private held = new Map<number, { v: number; on: number; gate?: GainNode }>();
  private sustain = false;
  private sustained = new Map<number, { v: number; on: number; gate?: GainNode }>();
  private recording: RawNote[] | null = null;

  onChange(l: () => void) { this.listeners.add(l); return () => { this.listeners.delete(l); }; }
  private emit() { this.listeners.forEach((l) => l()); }
  setPrefs(p: Partial<Prefs>) { this.prefs = { ...this.prefs, ...p }; try { localStorage.setItem(PREFS, JSON.stringify(this.prefs)); } catch { /* privat */ } this.attach(); this.emit(); }

  /** Zugriff anfordern (einmalig, der Browser fragt nach „MIDI-Geräte“) */
  async enable() {
    if (!this.supported) { this.error = 'Dieser Browser unterstützt kein Web MIDI. Bitte Chrome oder Edge verwenden.'; this.emit(); return; }
    if (this.access) return;
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
      this.access.onstatechange = () => this.attach();
      this.error = '';
      this.attach();
    } catch (e) {
      this.error = `Kein Zugriff auf MIDI-Geräte: ${(e as Error).message}. Im Browser für diese Seite erlauben.`;
      this.emit();
    }
  }

  private attach() {
    if (!this.access) return;
    this.devices = [];
    this.access.inputs.forEach((inp) => {
      this.devices.push({ id: inp.id, name: inp.name ?? 'MIDI-Eingang', manufacturer: inp.manufacturer ?? '' });
      inp.onmidimessage = this.prefs.disabled.includes(inp.id) ? null : (e) => this.message(inp.id, e);
    });
    this.emit();
  }

  /** Verarbeitung einer MIDI-Nachricht (öffentlich für Tests) */
  message(deviceId: string, e: { data: Uint8Array | null; timeStamp: number }) {
    const d = e.data;
    if (!d || d.length < 2) return;
    const type = d[0] & 0xf0, t = e.timeStamp || performance.now();
    this.activity.set(deviceId, t);
    if (type === 0x90 && d[2] > 0) this.noteOn(d[1], d[2] / 127, t);
    else if (type === 0x80 || (type === 0x90 && d[2] === 0)) this.noteOff(d[1], t);
    else if (type === 0xb0 && d[1] === 64) this.pedal(d[2] >= 64, t);
    else if (type === 0xb0 && (d[1] === 123 || d[1] === 120)) { for (const m of [...this.held.keys()]) this.noteOff(m, t); this.pedal(false, t); }
  }

  private noteOn(m: number, v: number, t: number) {
    // gleiche Taste nochmal gedrückt, während das Pedal hält: alte Note endet hier
    const s = this.sustained.get(m);
    if (s) { this.finish(m, s, t); this.sustained.delete(m); }
    const gate = this.prefs.thru ? this.thru(m, v) : undefined;
    this.held.set(m, { v, on: t, gate });
    this.lastNote = { m, v };
    this.emit();
  }
  private noteOff(m: number, t: number) {
    const h = this.held.get(m);
    if (!h) return;
    this.held.delete(m);
    if (this.sustain) this.sustained.set(m, h);
    else this.finish(m, h, t);
  }
  private pedal(down: boolean, t: number) {
    this.sustain = down;
    if (!down) { for (const [m, h] of this.sustained) this.finish(m, h, t); this.sustained.clear(); }
  }
  private finish(m: number, h: { v: number; on: number; gate?: GainNode }, t: number) {
    if (h.gate && engine.ctx) { const now = engine.ctx.currentTime; h.gate.gain.setTargetAtTime(0, now, 0.06); const g = h.gate; setTimeout(() => g.disconnect(), 800); }
    if (this.recording && h.on >= this.recStartPerf) this.recording.push({ m, v: h.v, on: h.on, off: Math.max(t, h.on + 5) });
  }

  /** Live über das Instrument der scharf geschalteten (sonst der ausgewählten) MIDI-Spur spielen */
  private thru(m: number, v: number): GainNode | undefined {
    const ctx = engine.ctx, st = useStudio.getState();
    if (!ctx || !engine.graph) { void engine.ensure(); return undefined; }
    const tracks = st.project.tracks.filter((t) => t.kind === 'midi' && t.armed);
    const t = tracks[0] ?? st.project.tracks.find((x) => x.id === st.selection.trackId && x.kind === 'midi');
    if (!t) return undefined;
    const gate = ctx.createGain();
    gate.connect(engine.graph.strip(t.id).input);
    voiceFor(t.instrument?.plugin, t.instrument?.preset)(ctx, gate, m, v, ctx.currentTime + 0.002, 8);
    return gate;
  }

  // ---- Aufnahme ----
  private recStartPerf = 0;
  startRecording() { this.recording = []; this.recStartPerf = performance.now() - 50; }
  /** Aufnahme beenden; noch gehaltene Noten enden jetzt */
  stopRecording(): RawNote[] {
    const now = performance.now();
    for (const [m, h] of this.held) this.finish(m, { ...h, gate: undefined }, now);
    for (const [m, h] of this.sustained) this.finish(m, { ...h, gate: undefined }, now);
    const out = this.recording ?? [];
    this.recording = null;
    return out;
  }
  get isRecording() { return !!this.recording; }
}

export const midiIn = new MidiIn();
